import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * One button. The app had three implementations of the same idea — `button.ghost`,
 * `.btnlike` (because .ghost is element-scoped and anchors couldn't use it) and
 * `.linklike` — so a link that acts like a button and a button that acts like a
 * link each looked slightly different. `Button` and `ButtonLink` share one set of
 * variants, and `ButtonLink` is what retires `.btnlike`.
 */
export type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md';

interface Shared {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

function classes(variant: ButtonVariant, size: ButtonSize, extra?: string): string {
  return cx('ui-btn', `ui-btn--${variant}`, `ui-btn--${size}`, extra);
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  className,
  children,
  ...rest
}: Shared & { loading?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className={classes(variant, size, className)}
      disabled={rest.disabled === true || loading}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="ui-btn__spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function ButtonLink({
  variant = 'ghost',
  size = 'md',
  className,
  children,
  ...rest
}: Shared & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a {...rest} className={classes(variant, size, className)}>
      {children}
    </a>
  );
}
